import React, { useMemo } from 'react';

import CasinoTransaction from '../../../../casino/Api/Models/CasinoTransaction';
import TrueFalseIcon from '../../../../components/common/TrueFalseIcon';
import { formatCurrency } from '../../../../utils/formatCurrency';
import Search from '../../../elements/Search';
import Table from '../../../elements/Table';
import Card from '../../../elements/common/Card';
import FilterByColumn, { Filter } from '../../../elements/common/FilterByColumn';
import SortButton, { SortProps } from '../../../elements/common/SortButton';
import TableLoader from '../../../elements/common/TableLoader';
import Paginator, { PaginatorProps } from '../../../elements/paginator';
import { formatDateTimeWithTimeZone } from '../../../helpers/DateFormatter';

import { ColumnFilters, FilterChangeCallback } from 'crm/elements/common/types';

interface Props {
    transactions: CasinoTransaction[];
    sortProps: SortProps;
    isLoading: boolean;
    autoSearch: {
        filterTable: (value: string) => void;
    };
    autoFilter: {
        columnFilters: ColumnFilters;
        onFilterChange: FilterChangeCallback;
    };
    autoPaginator: PaginatorProps;
    hideUserDetails?: boolean;
}

export enum CasinoTransactionTableStatuses {
    ALL = 'all',
}

function TransactionsTable({
    transactions,
    isLoading,
    sortProps,
    autoSearch,
    autoFilter,
    autoPaginator,
    hideUserDetails = false,
}: Props) {
    const defaultCurrency = process.env.MIX_CURRENCY;
    const columns = useMemo(() => {
        const baseColumns = [
            {
                Header: <SortButton column="external_token" label="External Token" {...sortProps} />,
                accessor: 'external_token',
            },
            {
                Header: <SortButton column="amount_manager" label="Amount" {...sortProps} />,
                accessor: 'amount',
                Cell: ({ value }) => formatCurrency(value, 2, defaultCurrency),
            },
            {
                Header: <SortButton column="action" label="Action" {...sortProps} />,
                accessor: 'action',
            },
            {
                Header: <SortButton column="is_canceled" label="Is canceled" {...sortProps} />,
                accessor: 'is_canceled',
                Cell: ({ value }: { value: boolean }) => <TrueFalseIcon status={value} />,
            },
            {
                Header: <SortButton column="created_at" {...sortProps} />,
                accessor: 'created_at',
                Cell: ({ value }) => formatDateTimeWithTimeZone(value),
            },
        ];

        // Insert additional columns for user details in baseColumns array
        if (hideUserDetails) {
            baseColumns.splice(
                0,
                0,
                {
                    Header: <SortButton column="casino_games.creator_name" label="Game provider" {...sortProps} />,
                    accessor: 'casino_game.creator_name',
                },
                {
                    Header: <SortButton column="casino_games.name" label="Game name" {...sortProps} />,
                    accessor: 'casino_game.name',
                }
            );
        }

        return baseColumns;
    }, [hideUserDetails, sortProps]);

    return (
        <Card title="All bets">
            <Search {...autoSearch} />
            <FilterByColumn {...autoFilter}>
                <Filter column="amount_manager" label="Amount" type="numberRange" />
                <Filter column="is_canceled" label="Is canceled" type="select" options={{ 1: 'Yes', 0: 'No' }} />
                <Filter
                    column="action"
                    label="Action"
                    type="select"
                    options={{ bet: 'bet', win: 'win', refund: 'refund', rollback: 'rollback', tip: 'tip' }}
                />
                <Filter column="casino_transactions.created_at" label="Created at" type="dateRange" />
            </FilterByColumn>
            <TableLoader isLoading={isLoading}>
                <Table columns={columns} data={transactions} additionalClasses="model-list" disableFixedLayout />
            </TableLoader>
            <Paginator {...autoPaginator} />
        </Card>
    );
}

export default TransactionsTable;
